import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import WCApproved from "@/components/WCApproved";
import CampaignReel from "@/components/CampaignReel";
import { FUTURE_DIVISION_LABELS } from "@/data/brandUniverse";

// Original diner shot — used as a last-resort fallback only
// when the homepage photo pool is genuinely empty.
const FALLBACK_HERO =
  "https://customer-assets.emergentagent.com/job_chaotic-threads/artifacts/2j36xdpo_18A59D19-9E21-430B-B766-CD44A58547F2.png";

export default function Home() {
  const [collections, setCollections] = useState([]);
  // null = waiting on the API. We show a dark editorial placeholder rather
  // than the fallback image, so refreshing the page doesn't flash the old
  // diner shot every time.
  const [heroImage, setHeroImage] = useState(null);

  useEffect(() => {
    api.get("/collections").then((r) => {
      setCollections(r.data || []);
    });
    // Pull ONE random photo from the dedicated HERO pool (separate from the
    // six-division reels). MongoDB $sample backs this, so it changes on each
    // page-load. Falls back to the original campaign shot only if the pool
    // is empty or the request fails.
    api
      .get("/homepage-photos", {
        params: { category: "hero", limit: 1, shuffle: true },
      })
      .then((r) => {
        const pick = (r.data || [])[0];
        if (!pick) {
          setHeroImage(FALLBACK_HERO);
          return;
        }
        // Prefer a mobile-specific variant on narrow viewports if the admin
        // has uploaded one.
        const isMobile =
          typeof window !== "undefined" && window.innerWidth < 640;
        const chosen =
          isMobile && pick.mobile_image_url
            ? pick.mobile_image_url
            : pick.image_url;
        setHeroImage(chosen ? fileUrl(chosen) : FALLBACK_HERO);
      })
      .catch(() => {
        setHeroImage(FALLBACK_HERO);
      });
  }, []);

  void collections;

  // Pick a single rotating subhead for the "Future Divisions" block per
  // page-load so it feels like a slow-rotating editorial spotlight rather
  // than a flickering carousel.
  const futureLabel = useMemo(
    () =>
      FUTURE_DIVISION_LABELS[
        Math.floor(Math.random() * FUTURE_DIVISION_LABELS.length)
      ],
    []
  );

  return (
    <div data-testid="home-page">
      <SEO
        path="/"
        description="Wet Cabbage. Streetwear for people who've eaten a kebab in a car park with dignity. Six divisions documenting modern leisure behaviour."
      />

      {/* HERO — full-bleed editorial campaign shot, FIRST thing in the
          viewport. Random pick from the homepage photo pool, rotates on
          each page-load. Sized to fill the screen on entry so it reads
          like a fashion-house cover shot. */}
      <section
        className="relative bg-[#0F0F0F] text-[#F9F9F4]"
        data-testid="hero-campaign"
      >
        <div className="relative mx-auto aspect-[4/5] w-full max-w-[1400px] sm:aspect-[16/9]">
          {heroImage ? (
            <img
              src={heroImage}
              alt="Wet Cabbage editorial campaign"
              className="block h-full w-full object-cover object-center"
              data-testid="hero-campaign-image"
            />
          ) : (
            <div
              className="block h-full w-full bg-[#0F0F0F]"
              aria-hidden
              data-testid="hero-campaign-placeholder"
            />
          )}
        </div>
      </section>

      {/* WORDMARK BAND — sits beneath the campaign hero */}
      <section className="bg-[#F9F9F4]" data-testid="wordmark-band">
        <div className="mx-auto flex max-w-[1400px] flex-col items-center px-6 pb-6 pt-10 md:px-10 md:pb-10 md:pt-14">
          <img
            src="/brand/wet-cabbage-skegness.jpg"
            alt="Wet Cabbage — Skegness"
            className="h-auto w-[92%] max-w-[380px] object-contain mix-blend-multiply sm:max-w-[460px] md:max-w-[540px] lg:max-w-[640px]"
            data-testid="hero-logo-mascot"
          />
          <p
            className="font-display mt-5 max-w-4xl text-balance text-center text-[15px] font-extrabold uppercase leading-[1.15] tracking-[0.02em] text-[#1A3626] sm:text-2xl sm:leading-tight sm:tracking-[0.03em] md:mt-7 md:text-3xl md:tracking-[0.05em] lg:text-[42px] lg:tracking-[0.06em]"
            data-testid="brand-tagline"
          >
            Streetwear for people who&rsquo;ve eaten a kebab in a car park with dignity
          </p>
        </div>
      </section>

      {/* WC APPROVED — sitewide quality control band */}
      <WCApproved tone="dark" />

      {/* DIVISION 01 — TRAVEL */}
      <DivisionHeader
        number="01"
        title="Travel Division"
        body="Early airport pints, overpacked suitcases, sunbed diplomacy, motel energy and the dignified chaos of being abroad."
      />
      <CampaignReel category="travel" compact />

      {/* DIVISION 02 — EVERYDAY LIFE */}
      <DivisionHeader
        number="02"
        title="Everyday Life"
        body="Aldi expeditions. Petrol station elegance. Meal deals, retail parks, takeaway runs and the deeply average grandeur of modern weekday existence."
      />
      <CampaignReel category="everyday" compact />

      {/* DIVISION 03 — PUB */}
      <DivisionHeader
        number="03"
        title="Pub Division"
        body="Beer garden survival gear. Quiz-night confidence. Pub carpets, suspicious toilets, weird regulars and the spiritual mathematics of &lsquo;just one pint&rsquo;."
      />
      <CampaignReel category="pub" compact />

      {/* DIVISION 04 — SPORTS & LEISURE */}
      <DivisionHeader
        number="04"
        title="Sports & Leisure"
        body="Performancewear for people who own a protein shaker but rarely a gym schedule. Tracksuits for casual inactivity. Athletic clothing, optimistically purchased."
      />
      <CampaignReel category="sports" compact />

      {/* DIVISION 05 — DAD LIFE */}
      <DivisionHeader
        number="05"
        title="Dad Life Division"
        body="BBQ disasters. Lawn mowing pride. Garage beers, thermostat politics, DIY confidence and the slow art of carrying all the shopping in one trip."
      />
      <CampaignReel category="dadlife" compact />

      {/* DIVISION 06 — HOME */}
      <DivisionHeader
        number="06"
        title="Home Division"
        body="Flat-packed furniture chaos. Leftover screws. Badly assembled shelving, washing-machine confusion and the quiet tragedy of modern domestic survival."
      />
      <CampaignReel category="home" compact />

      {/* ============================================================
         Closing — restrained CTA strip
         ============================================================ */}
      <section className="bg-[#F9F9F4]">
        <div className="mx-auto max-w-[1400px] px-6 pb-24 pt-16 md:px-10 md:pb-32 md:pt-20">
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/shop"
              className="font-display inline-flex items-center gap-3 border-2 border-[#1A3626] bg-[#1A3626] px-7 py-4 text-xs font-black uppercase tracking-[0.32em] text-[#F9F9F4] transition-all hover:-translate-y-0.5 hover:bg-transparent hover:text-[#1A3626] md:px-9 md:py-5 md:text-sm md:tracking-[0.36em]"
              data-testid="hero-shop-cta"
            >
              Shop everything
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
         FUTURE DIVISIONS — was "Coming Soon", now framed as fashion-house
         R&D. Subhead rotates per page-load (Moistwear Labs / Future
         Divisions / Under Investigation By Professionals).
         ============================================================ */}
      <FutureDivisionsHeader label={futureLabel} />
      <CampaignReel category="coming" compact />
    </div>
  );
}

/* ---------- Internal components ---------- */

function DivisionHeader({ number, title, body }) {
  return (
    <section
      className="bg-[#F9F9F4]"
      data-testid={`division-header-${number}`}
    >
      <div className="mx-auto max-w-[1400px] px-6 pb-6 pt-8 md:px-10 md:pb-8 md:pt-12">
        <div className="flex flex-col gap-2 md:flex-row md:items-baseline md:gap-8">
          {/* DIVISION 0X kicker */}
          <div className="shrink-0">
            <div className="font-display text-[10px] font-bold uppercase tracking-[0.42em] text-[#93C065] md:text-xs">
              Division {number}
            </div>
          </div>
          <div className="flex-1">
            <h3 className="font-display text-3xl font-black uppercase leading-[0.95] tracking-tight text-[#1A3626] md:text-5xl lg:text-6xl">
              {title}
            </h3>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#1A3626]/85 md:text-lg md:leading-[1.65]">
              {body}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function FutureDivisionsHeader({ label }) {
  return (
    <section
      className="bg-[#F9F9F4]"
      data-testid="future-divisions-header"
    >
      <div className="mx-auto max-w-[1400px] px-6 pb-6 pt-20 md:px-10 md:pb-10 md:pt-32">
        <div className="mx-auto max-w-3xl text-center">
          <div
            className="font-display text-[10px] font-bold uppercase tracking-[0.42em] text-[#93C065] md:text-xs"
            data-testid="future-divisions-label"
          >
            {label}
          </div>
          <h3 className="font-display mt-4 text-3xl font-black uppercase leading-[0.95] tracking-tight text-[#1A3626] sm:text-4xl md:text-5xl lg:text-6xl">
            Currently under investigation.
          </h3>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#1A3626]/85 md:text-lg md:leading-[1.65]">
            Future denominations of the Wet Cabbage universe. Researched
            seriously. Released eventually. Patience is a feature.
          </p>
        </div>
      </div>
    </section>
  );
}
